import { useCallback, useMemo } from "react";
import { createColumnHelper, tableFeatures } from "@tanstack/react-table";
import { formatIDArt } from "@/ts/util";
import { Constantes } from "@/ts/constantes";
import { CommonFeatures } from "@/hooks/useTanstackTable";
import { TypedTable } from "@/components/ui/table/TypedTable";
import type { Fase, InfoComponente } from "@/hooks/Api/OrdenesFabricacion";

interface TablaComponentesProps {
	readonly data: InfoComponente[] | null;
	readonly setData: Setter<InfoComponente[]>;
	readonly fasesRuta: Fase[];
}
export function TablaComponentes(props: TablaComponentesProps) {
	const { setData, fasesRuta } = props;

	const removeComponent = useCallback((idComponente: string | number) => {
		setData(prev => prev.filter(componente => componente.idComponente !== idComponente));
	}, [setData]);

	const updateComponentFase = useCallback((idComponente: string, valueStr: string) => {
		const nuevaFase = valueStr === "" ? null : Number(valueStr);
		setData(prev => prev.map(componente =>
			componente.idComponente === idComponente ? { ...componente, faseUsado: nuevaFase } : componente
		));
	}, [setData]);

	const { columns, features } = useMemo(
		() => Columns(fasesRuta, removeComponent, updateComponentFase),
		[fasesRuta, removeComponent, updateComponentFase]
	);

	return (
		<TypedTable
			// scrollY="400px"
			className="dataTable display"
			features={features}
			columns={columns}
			data={props.data ?? []}

			noResultsMessage="No se encontraron resultados"
			errorMessage="Ocurrió un error"
			isLoading={false}
			isError={false}
		/>
	);
}

function Columns(fasesRuta: Fase[], removeComponent: (idComponente: string | number) => void, updateComponentFase: (idComponente: string, valueStr: string) => void,) {
	const columnHelper = createColumnHelper<typeof features, InfoComponente>();

	const features = tableFeatures(CommonFeatures);

	const columns = [
		columnHelper.accessor("idComponente", {
			header: "ID",
			meta: { className: "center" },
			cell: info => formatIDArt(info.getValue(), 0)
		}),
		columnHelper.accessor("descComponente", {
			header: "Descripción",
			meta: { className: "center" }
		}),
		columnHelper.accessor("cantidadPorArticulo", {
			header: "Cantidad por conjunto",
			meta: { className: "center" },
			cell: (info) => getFraccion(info.getValue()),
		}),
		columnHelper.accessor("faseUsado", {
			header: "Fase",
			meta: { className: "center" },
			cell: (info) => (
				<SelectorFase
					fasesRuta={fasesRuta}
					value={String(info.getValue() ?? "")}
					onChange={(value) => { updateComponentFase(info.row.original.idComponente, value); }}
				/>
			),
		}),

		Constantes.elegirComponentesOF ? (
			columnHelper.display({
				id: "eliminar",
				header: "",
				meta: { className: "center" },
				cell: info => (
					<BtnEliminar onClick={() => { removeComponent(info.row.original.idComponente); }} />
				),
			})
		) : undefined,
	].filter((col) => col != null);

	return { columns: columnHelper.columns(columns), features };
}

function BtnEliminar(props: { readonly onClick: () => void; }) {
	return (
		<div style={{ display: "flex", justifyContent: "center" }}>
			<button className="eliminarComponente" type="button" onClick={props.onClick}>
				<i className="fa fa-trash" />
			</button>
		</div>
	);
}
function SelectorFase(props: { readonly fasesRuta: Array<{ xfaseruta_id: string; }>; readonly value: string; readonly onChange: (value: string) => void; }) {
	const style = { width: "100%" } as const;
	if (props.fasesRuta.length === 0) return <div style={style}>-</div>;
	if (props.fasesRuta.length == 1) return <div style={style}>{`F ${props.fasesRuta[0].xfaseruta_id}`}</div>;

	return (
		<select
			value={props.value}
			onChange={(event) => { props.onChange(event.target.value); }}
			className="form-control"
			style={style}
		>
			<option value="">-- Asignar --</option>
			{props.fasesRuta.map(faseRuta => (
				<option key={faseRuta.xfaseruta_id} value={faseRuta.xfaseruta_id}>
					F {faseRuta.xfaseruta_id}
				</option>
			))}
		</select>
	);
}

function getFraccion(cantidadPorArticulo: number) {
	const formatter = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 8, minimumFractionDigits: 0 });
	if (cantidadPorArticulo < 1 && cantidadPorArticulo > -1) {
		return (
			<span>
				<span>1</span>
				<span>÷</span>
				<span>{Math.round(1 / cantidadPorArticulo)}</span>
				<span>=</span>
				<span>{formatter.format(cantidadPorArticulo)}</span>
			</span>
		);
	} else {
		return formatter.format(cantidadPorArticulo);
	}
}
